import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import {
  ContactService,
  TicketDetails,
  TicketResponse,
  UserTicket,
} from '../../services/contact.service';
import { AuthService, User } from '../../services/auth.service';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';

@Component({
  selector: 'app-ticket-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './ticket-details.component.html',
  styleUrls: ['./ticket-details.component.css'],
})
export class TicketDetailsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  ticketId: string = '';
  ticketDetails: TicketDetails | null = null;
  currentUser: User | null = null;
  isLoading = false;
  isLoadingReply = false;
  errorMessage = '';
  successMessage = '';

  replyForm: FormGroup;
  showReplyForm = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contactService: ContactService,
    private authService: AuthService,
    private fb: FormBuilder
  ) {
    this.replyForm = this.fb.group({
      message: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(5000),
        ],
      ],
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.ticketId = params.get('id') || '';
      if (this.ticketId) {
        this.loadTicketDetails();
      }
    });

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTicketDetails(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.contactService
      .getTicketById(this.ticketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details) => {
          this.ticketDetails = details;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading ticket details:', error);
          this.errorMessage = error.message || 'Failed to load ticket details';
          this.isLoading = false;
        },
      });
  }

  toggleReplyForm(): void {
    this.showReplyForm = !this.showReplyForm;
    if (this.showReplyForm) {
      this.replyForm.reset();
      this.clearMessages();
    }
  }

  submitReply(): void {
    if (this.replyForm.invalid) {
      this.markFormGroupTouched(this.replyForm);
      return;
    }

    this.isLoadingReply = true;
    this.errorMessage = '';

    const replyData = {
      message: this.replyForm.get('message')?.value.trim(),
    };

    this.contactService
      .addTicketReply(this.ticketId, replyData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Reply sent successfully';
          this.showReplyForm = false;
          this.replyForm.reset();
          this.isLoadingReply = false;
          this.loadTicketDetails();
        },
        error: (error) => {
          console.error('Error adding reply:', error);
          this.errorMessage = error.message || 'Failed to send reply';
          this.isLoadingReply = false;
        },
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  goBack(): void {
    this.router.navigate(['/tickets']);
  }

  getStatusClass(status: string): string {
    return this.contactService.getStatusClass(status);
  }

  getPriorityClass(priority: string): string {
    return this.contactService.getPriorityClass(priority);
  }

  formatStatus(status: string): string {
    return this.contactService.formatStatus(status);
  }

  formatPriority(priority: string): string {
    return this.contactService.formatPriority(priority);
  }

  formatDate(dateString: string): string {
    return this.contactService.formatTicketDate(dateString);
  }

  formatFullDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return 'Invalid date';
    }
  }

  getTicketIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'in_progress':
        return '🔄';
      case 'resolved':
        return '✅';
      case 'closed':
        return '🔒';
      default:
        return '📝';
    }
  }

  isAdminResponse(response: TicketResponse): boolean {
    return response.is_admin_response && response.is_sent_to_customer;
  }

  isUserResponse(response: TicketResponse): boolean {
    return !response.is_admin_response || !response.is_sent_to_customer;
  }

  getResponseAuthor(response: TicketResponse): string {
    if (this.isAdminResponse(response)) {
      return (
        `${response.admin_first_name || 'Admin'} ${
          response.admin_last_name || ''
        }`.trim() || 'Support Team'
      );
    }
    return this.currentUser
      ? `${this.currentUser.first_name} ${this.currentUser.last_name}`
      : 'You';
  }

  getResponseText(response: TicketResponse): string {
    if (response.response_text.startsWith('User Reply from ')) {
      const match = response.response_text.match(/User Reply from [^:]+: (.+)/);
      return match ? match[1] : response.response_text;
    }
    return response.response_text;
  }

  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  canReply(): boolean {
    if (!this.ticketDetails) return false;
    const status = this.ticketDetails.ticket.status;
    return status !== 'closed';
  }

  getCharacterCount(): number {
    return this.replyForm.get('message')?.value?.length || 0;
  }

  getMaxCharacters(): number {
    return 5000;
  }

  trackByResponseId(index: number, response: TicketResponse): string {
    return response.id;
  }

  createNewTicket(): void {
    this.router.navigate(['/contact-us']);
  }
}
